// src/cockpit4/components/DecisionSupportPanel.tsx
// ============================================
// 决策支持（v5.2 审计修复 P2-④ · 白皮书第十三章：决策权与执行权分离）
//
// 策略层：系统出 3 套方案 → 人（CEO）选 1 套 → 选择结果写回 Obsidian（Rule 05）
// 实现：输入决策问题 → HermesRuntime(LLM) 真实生成 3 套方案（结构/成本/风险/收益）
//       → 用户选择一套 → 写入 Vault XEAOS/Decisions/（可追溯，Rule 04）
// 禁止 Mock：无输入不生成；LLM 失败显示错误。
// ============================================

import { useState } from "react";
import { Lightbulb, Loader2, Send, CheckCircle2, ArrowRight, FileText } from "lucide-react";
import RuntimeManager from "../../runtime/RuntimeManager";
import { RuntimeName } from "../../types/runtime";

interface PlanOption {
  name: string;
  summary: string;
  cost: string;
  risk: string;
  benefit: string;
}

export default function DecisionSupportPanel() {
  const [question, setQuestion] = useState("");
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [plans, setPlans] = useState<PlanOption[]>([]);
  const [selected, setSelected] = useState<number | null>(null);
  const [saveResult, setSaveResult] = useState<string | null>(null);

  const waitInit = async () => {
    for (let i = 0; i < 100; i++) {
      if (RuntimeManager.isInitialized()) break;
      await new Promise((r) => setTimeout(r, 200));
    }
    return RuntimeManager.isInitialized();
  };

  // LLM 生成 3 套方案（HermesRuntime chat → DeepSeek）
  const handleGenerate = async () => {
    const q = question.trim();
    if (!q) {
      setError("请输入需要决策的问题");
      return;
    }
    setGenerating(true);
    setError(null);
    setPlans([]);
    setSelected(null);
    setSaveResult(null);
    try {
      await waitInit();
      const prompt = `请为以下决策问题提供 3 套可选方案，每套方案包含：方案名称、一句话概述、成本、风险、收益。
以 JSON 数组格式输出，不要额外文字：
[{"name":"方案A名称","summary":"概述","cost":"成本描述","risk":"风险描述","benefit":"收益描述"}]
决策问题：${q}`;
      const resp = await RuntimeManager.execute(RuntimeName.Hermes, "chat", { content: prompt } as any);
      const r = resp?.data as any;
      const text = typeof r === "string" ? r : (r?.content || r?.response || "");
      const jsonMatch = text.match(/\[[\s\S]*\]/);
      if (!jsonMatch) {
        setError("LLM 未返回结构化方案，请重试");
        return;
      }
      const parsed = JSON.parse(jsonMatch[0]);
      if (!Array.isArray(parsed) || parsed.length === 0) {
        setError("方案解析失败");
        return;
      }
      setPlans(parsed.slice(0, 3).map((p: any) => ({
        name: p.name || "未命名方案",
        summary: p.summary || "",
        cost: p.cost || "未说明",
        risk: p.risk || "未说明",
        benefit: p.benefit || "未说明",
      })));
    } catch (err: any) {
      setError(err?.message || "生成方案失败");
    } finally {
      setGenerating(false);
    }
  };

  // 选择方案 → 写回 Obsidian（Rule 05）
  const handleSelect = async (idx: number) => {
    setSelected(idx);
    setSaveResult(null);
    try {
      const plan = plans[idx];
      const today = new Date().toISOString().slice(0, 10);
      const ts = Date.now();
      const safeQ = question.slice(0, 20).replace(/[\/\\:*?"<>|]/g, "_");
      const filePath = `XEAOS/Decisions/${today}-${safeQ}-${ts}.md`;
      const content = [
        "---",
        "type: decision",
        `date: ${today}`,
        `question: "${question.replace(/"/g, '\\"')}"`,
        `selected: ${idx + 1}`,
        `selectedName: "${plan.name.replace(/"/g, '\\"')}"`,
        "---",
        "",
        `# 决策记录 · ${question}`,
        "",
        `**选择方案**：${plan.name}`,
        "",
        "## 3 套方案",
        ...plans.map((p, i) => `### ${i + 1}. ${p.name}${i === idx ? "（已选择）" : ""}\n- 概述：${p.summary}\n- 成本：${p.cost}\n- 风险：${p.risk}\n- 收益：${p.benefit}`),
        "",
      ].join("\n");
      const writeResp = await fetch(`/vault-api/write?path=${encodeURIComponent(filePath)}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ content }),
      });
      const writeJson = await writeResp.json();
      setSaveResult(writeJson.success ? `✅ 决策已写入 Obsidian：${filePath}` : `❌ 写入失败：${JSON.stringify(writeJson)}`);
    } catch (err: any) {
      setSaveResult(`❌ 写入异常：${err?.message}`);
    }
  };

  return (
    <div className="h-full overflow-auto p-4 space-y-3">
      <div className="rounded-lg border border-purple-500/20 bg-purple-500/[0.03] p-3">
        <div className="flex items-start gap-2">
          <Lightbulb size={13} className="text-purple-400 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-[11px] font-medium text-gray-300">决策支持（3 套方案 → 人选 1）</p>
            <p className="text-[9px] font-mono text-gray-600 leading-relaxed mt-0.5">
              白皮书第十三章：AI 只有建议权，没有最终决策权。输入决策问题 → 系统生成 3 套方案（含成本/风险/收益）→
              CEO 选择 1 套 → 决策记录写入 Obsidian 可追溯（Rule 04/05）。
            </p>
          </div>
        </div>
      </div>

      <div className="flex gap-2">
        <input
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleGenerate()}
          placeholder="输入需要决策的问题，如：下半年是否要增加酒店 SaaS 的渠道合作？"
          className="flex-1 px-3 py-2 rounded-lg bg-white/[0.04] border border-white/[0.08] text-[12px] text-gray-200 outline-none focus:border-purple-500/40"
        />
        <button
          onClick={handleGenerate}
          disabled={generating || !question.trim()}
          className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-purple-600/80 hover:bg-purple-600 text-[10px] font-mono text-white transition disabled:opacity-40"
        >
          {generating ? <Loader2 size={11} className="animate-spin" /> : <Send size={11} />}
          {generating ? "生成中" : "生成 3 套方案"}
        </button>
      </div>

      {error && <div className="text-[10px] font-mono text-red-400">{error}</div>}

      {plans.length > 0 && (
        <div className="space-y-2">
          <div className="text-[10px] font-mono text-gray-500">3 套可选方案（点击选择一套并写入 Obsidian）：</div>
          {plans.map((p, i) => {
            const active = selected === i;
            return (
              <button
                key={i}
                onClick={() => handleSelect(i)}
                className={`w-full text-left rounded-xl border p-3 transition ${
                  active ? "border-purple-500/50 bg-purple-500/[0.08]" : "border-white/[0.06] bg-white/[0.02] hover:bg-white/[0.04]"
                }`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="flex items-center gap-1.5 text-[11px] font-medium text-gray-200">
                    <span className="w-5 h-5 rounded-full bg-purple-500/15 border border-purple-500/40 flex items-center justify-center text-[9px] font-mono text-purple-300">
                      {i + 1}
                    </span>
                    {p.name}
                    {active && <CheckCircle2 size={11} className="text-emerald-400" />}
                  </span>
                  <ArrowRight size={10} className="text-gray-600" />
                </div>
                <p className="text-[9px] font-mono text-gray-400 mb-1">{p.summary}</p>
                <div className="grid grid-cols-3 gap-1.5 text-[8px] font-mono">
                  <div className="rounded bg-white/[0.02] p-1"><span className="text-gray-600">成本 </span><span className="text-amber-300">{p.cost}</span></div>
                  <div className="rounded bg-white/[0.02] p-1"><span className="text-gray-600">风险 </span><span className="text-red-300">{p.risk}</span></div>
                  <div className="rounded bg-white/[0.02] p-1"><span className="text-gray-600">收益 </span><span className="text-emerald-300">{p.benefit}</span></div>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {saveResult && (
        <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/[0.03] p-2.5 text-[10px] font-mono text-emerald-300 flex items-start gap-1.5">
          <FileText size={10} className="mt-0.5 flex-shrink-0" />
          {saveResult}
        </div>
      )}
    </div>
  );
}
